/**
 * Global color-theme registry — 10 themes, applied via `data-color-theme` on
 * <html> alongside the existing light/`.dark` class. See THEME_SPEC.md.
 *
 * The actual CSS variable values for each theme live in `src/index.css`
 * (light `[data-color-theme="X"]{}` + dark `.dark[data-color-theme="X"]{}`).
 * This module is the single source of truth for the theme NAMES + UI metadata
 * (label, description, swatch color for the picker).
 */

export type ThemeName =
  | 'ocean'
  | 'forest'
  | 'sunset'
  | 'rose'
  | 'midnight'
  | 'navy'
  | 'graphite'
  | 'crimson'
  | 'teal'
  | 'amber'

export interface ThemeMeta {
  /** Stable id (matches `data-color-theme` value). */
  name: ThemeName
  /** Human label (FR). */
  label: string
  /** Short description of the identity. */
  description: string
  /** Swatch color for the picker — the theme's signature/primary hue (HSL triplet). */
  swatch: string
  /** Top-bar color used for a stronger swatch ring (HSL triplet, light mode). */
  topbar: string
}

export const themes: Record<ThemeName, ThemeMeta> = {
  ocean: {
    name: 'ocean',
    label: 'Océan',
    description: 'Bleu ciel — lumineux et apaisant',
    swatch: '199 89% 48%',
    topbar: '200 70% 22%',
  },
  forest: {
    name: 'forest',
    label: 'Forêt',
    description: 'Vert émeraude — naturel et sobre',
    swatch: '152 55% 40%',
    topbar: '155 45% 16%',
  },
  sunset: {
    name: 'sunset',
    label: 'Coucher de soleil',
    description: 'Orange chaud — énergique',
    swatch: '24 90% 52%',
    topbar: '18 55% 22%',
  },
  rose: {
    name: 'rose',
    label: 'Rose',
    description: 'Rose vif — moderne et doux',
    swatch: '336 78% 56%',
    topbar: '340 45% 22%',
  },
  midnight: {
    name: 'midnight',
    label: 'Minuit',
    description: 'Indigo/violet — élégant et profond',
    swatch: '255 70% 60%',
    topbar: '245 42% 15%',
  },
  navy: {
    name: 'navy',
    label: 'Marine',
    description: 'Bleu marine classique — corporate',
    swatch: '217 72% 46%',
    topbar: '220 55% 16%',
  },
  graphite: {
    name: 'graphite',
    label: 'Graphite',
    description: 'Gris ardoise — neutre et pro',
    swatch: '220 9% 46%',
    topbar: '220 12% 20%',
  },
  crimson: {
    name: 'crimson',
    label: 'Cramoisi',
    description: 'Rouge profond — affirmé',
    swatch: '348 75% 48%',
    topbar: '350 50% 20%',
  },
  teal: {
    name: 'teal',
    label: 'Sarcelle',
    description: 'Turquoise/cyan — frais',
    swatch: '182 65% 38%',
    topbar: '185 55% 16%',
  },
  amber: {
    name: 'amber',
    label: 'Ambre',
    description: 'Ambre/or — chaleureux',
    swatch: '38 85% 48%',
    topbar: '36 50% 20%',
  },
}

/** Ordered list for the picker UI. */
export const themeList: ThemeMeta[] = [
  themes.ocean,
  themes.forest,
  themes.sunset,
  themes.rose,
  themes.midnight,
  themes.navy,
  themes.graphite,
  themes.crimson,
  themes.teal,
  themes.amber,
]

/** localStorage key for the active color theme (per-user, immediate). */
export const THEME_STORAGE_KEY = 'sgrc-color-theme'

/** Default theme when none has been selected. */
export const DEFAULT_THEME: ThemeName = 'ocean'

/** Type guard — is the given string one of the 10 valid theme names? */
export function isThemeName(value: unknown): value is ThemeName {
  return typeof value === 'string' && value in themes
}
